'use client';

import { cn } from '@/shared/lib/cn';

interface SettingsCardProps {
  children: React.ReactNode;
  title?: string;
  /** Optional trailing element on the header row, opposite the title —
   *  e.g. a section total and/or a "+" add action (Apple parity: the
   *  estimate sections render a plus opposite the section name). */
  headerAction?: React.ReactNode;
  /** Optional footer caption rendered in muted text below the card.
   *  Mirrors Apple `Section { ... } footer: { Text(...) }`. */
  footer?: string;
  className?: string;
  /** Override the inner surface background/styling. Defaults to the shared
   *  `bg-settings-card` panel tint; the estimate document surfaces pass
   *  `bg-estimate-paper` so the estimate reads as a cream paper sheet. */
  surfaceClassName?: string;
}

/**
 * Settings card component - iOS/Android style grouped settings panel
 * Used for grouping related settings items together
 */
export function SettingsCard({
  children,
  title,
  headerAction,
  footer,
  className,
  surfaceClassName,
}: SettingsCardProps) {
  // Section title sits ABOVE the rounded card (Apple/Android parity) as a small
  // uppercase eyebrow — secondary grey, light letter-spacing, inset by the same
  // 16-px gutter as the rows so it lines up with the row text. `space-y-1` keeps
  // it flush against the group (a tight gap, not a floating header). Card body
  // is the rounded surface grouping the rows — light-gray tint, no drop shadow,
  // mirroring macOS Form `.grouped` System-Settings cards.
  return (
    <div className={cn('space-y-1', className)}>
      {(title || headerAction) && (
        <div className="flex items-center justify-between gap-2 px-4 pt-1">
          {title && (
            <h3 className="text-section-eyebrow text-muted-foreground">
              {title}
            </h3>
          )}
          {headerAction}
        </div>
      )}
      {/* `py-1.5` on the surface gives the first/last rows extra breathing room
          at the card edges: each row carries `py-3` (12px), so the whitespace
          between two rows reads as 24px while the top/bottom edges would
          otherwise be a tighter 12px. The 6px surface padding lifts the edges
          to 18px so the group looks optically balanced top to bottom. */}
      <div
        className={cn(
          'bg-settings-card rounded-2xl overflow-hidden py-1.5',
          surfaceClassName
        )}
      >
        {children}
      </div>
      {footer && (
        <p className="text-card-meta text-muted-foreground px-4 pt-1">
          {footer}
        </p>
      )}
    </div>
  );
}
